import { PremiumHeader } from '@/components/premium/PremiumHeader';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useEffect } from 'react';

export default function Policies() {
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll to section if hash is present
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  return (
    <div className="min-h-screen noise-texture">
      <PremiumHeader />

      <div className="container mx-auto px-8 py-12 max-w-4xl">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Previous Page
        </button>

        <h1 className="text-4xl md:text-5xl font-black uppercase mb-8">Terms & Policies</h1>

        {/* Quick Links */}
        <div className="bg-secondary border border-border p-4 mb-8">
          <p className="text-xs uppercase tracking-wider font-bold mb-3">Quick Navigation</p>
          <div className="flex flex-wrap gap-3 text-sm">
            <a href="#shipping" className="text-primary hover:underline">Shipping & Delivery</a>
            <span className="text-muted-foreground">|</span>
            <a href="#privacy" className="text-primary hover:underline">Privacy Policy</a>
            <span className="text-muted-foreground">|</span>
            <a href="#cancellation" className="text-primary hover:underline">Cancellation & Refund</a>
            <span className="text-muted-foreground">|</span>
            <a href="#terms" className="text-primary hover:underline">Terms & Conditions</a>
            <span className="text-muted-foreground">|</span>
            <a href="#contact" className="text-primary hover:underline">Contact Us</a>
          </div>
        </div>

        <div className="space-y-12">
          {/* Shipping & Delivery Policy */}
          <section id="shipping" className="bg-white border border-border p-8 scroll-mt-32">
            <h2 className="text-2xl font-black uppercase mb-2">Shipping & Delivery Policy</h2>
            <p className="text-xs text-muted-foreground mb-6">Last updated on Dec 25, 2025</p>
            
            <div className="prose prose-sm max-w-none text-muted-foreground">
              <p className="mb-4">
                For International buyers, orders are shipped and delivered through registered international courier companies and/or International speed post only. For domestic buyers, orders are shipped through registered domestic courier companies and/or speed post only. Orders are shipped within 0-7 days or as per the delivery date agreed at the time of order confirmation and delivering of the shipment subject to Courier Company / post office norms.
              </p>
              <p className="mb-4">
                MODULAR FURNITURE STORE is not liable for any delay in delivery by the courier company / postal authorities and only guarantees to hand over the consignment to the courier company or postal authorities within 0-7 days from the date of the order and payment or as per the delivery date agreed at the time of order confirmation. Delivery of all orders will be to the address provided by the buyer. Delivery of our services will be confirmed on your mail ID as specified during registration.
              </p>
              <p>
                For any issues in utilizing our services you may contact our helpdesk on <strong>9820596811</strong> or <strong>hello@modularfurniturestore.com</strong>
              </p>
            </div>
          </section>

          {/* Privacy Policy */}
          <section id="privacy" className="bg-white border border-border p-8 scroll-mt-32">
            <h2 className="text-2xl font-black uppercase mb-2">Privacy Policy</h2>
            <p className="text-xs text-muted-foreground mb-6">Last updated on Dec 25, 2025</p>
            
            <div className="prose prose-sm max-w-none text-muted-foreground">
              <p className="mb-4">
                This privacy policy sets out how MODULAR FURNITURE STORE uses and protects any information that you give MODULAR FURNITURE STORE when you visit their website and/or agree to purchase from them. MODULAR FURNITURE STORE is committed to ensuring that your privacy is protected. Should we ask you to provide certain information by which you can be identified when using this website, and then you can be assured that it will only be used in accordance with this privacy statement. MODULAR FURNITURE STORE may change this policy from time to time by updating this page. You should check this page from time to time to ensure that you adhere to these changes.
              </p>
              
              <h3 className="text-lg font-bold text-foreground mt-6 mb-3">We may collect the following information:</h3>
              <ul className="list-disc pl-5 mb-4 space-y-1">
                <li>Name</li>
                <li>Contact information including email address</li>
                <li>Demographic information such as postcode, preferences and interests, if required</li>
                <li>Other information relevant to customer surveys and/or offers</li>
              </ul>

              <h3 className="text-lg font-bold text-foreground mt-6 mb-3">What we do with the information we gather:</h3>
              <ul className="list-disc pl-5 mb-4 space-y-1">
                <li>Internal record keeping</li>
                <li>We may use the information to improve our products and services</li>
                <li>We may periodically send promotional emails about new products, special offers or other information which we think you may find interesting</li>
                <li>From time to time, we may also use your information to contact you for market research purposes</li>
                <li>We may use the information to customise the website according to your interests</li>
              </ul>

              <p className="mb-4">
                We are committed to ensuring that your information is secure. In order to prevent unauthorised access or disclosure we have put in suitable measures.
              </p>

              <h3 className="text-lg font-bold text-foreground mt-6 mb-3">How we use cookies:</h3>
              <p className="mb-4">
                A cookie is a small file which asks permission to be placed on your computer's hard drive. Once you agree, the file is added and the cookie helps analyze web traffic or lets you know when you visit a particular site. Cookies allow web applications to respond to you as an individual. We use traffic log cookies to identify which pages are being used. This helps us analyze data about webpage traffic and improve our website in order to tailor it to customer needs. We only use this information for statistical analysis purposes and then the data is removed from the system.
              </p>
              <p className="mb-4">
                You can choose to accept or decline cookies. Most web browsers automatically accept cookies, but you can usually modify your browser setting to decline cookies if you prefer. This may prevent you from taking full advantage of the website.
              </p>

              <h3 className="text-lg font-bold text-foreground mt-6 mb-3">Controlling your personal information:</h3>
              <p className="mb-4">
                You may choose to restrict the collection or use of your personal information. If you have previously agreed to us using your personal information for direct marketing purposes, you may change your mind at any time by writing to or emailing us at <strong>hello@modularfurniturestore.com</strong>
              </p>
              <p>
                We will not sell, distribute or lease your personal information to third parties unless we have your permission or are required by law to do so.
              </p>
            </div>
          </section>

          {/* Cancellation & Refund Policy */}
          <section id="cancellation" className="bg-white border border-border p-8 scroll-mt-32">
            <h2 className="text-2xl font-black uppercase mb-2">Cancellation & Refund Policy</h2>
            <p className="text-xs text-muted-foreground mb-6">Last updated on Dec 25, 2025</p>
            
            <div className="prose prose-sm max-w-none text-muted-foreground">
              <p className="mb-4">
                MODULAR FURNITURE STORE believes in helping its customers as far as possible. Under this policy:
              </p>
              
              <p className="mb-4">
                To maintain the high standard of service and speed our customers expect, we have implemented a "No Cancellation or Refund" policy once an order is placed.
              </p>

              <h3 className="text-lg font-bold text-foreground mt-6 mb-3">Why this policy exists:</h3>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li><strong>Immediate Processing:</strong> Our fulfilment system is designed for speed. As soon as an order is confirmed, it is automatically sent to our warehouse or production team to be picked, packed, or prepared. This automation makes it nearly impossible to intercept an order without disrupting the entire logistics chain.</li>
                <li><strong>Inventory Commitment:</strong> Once you place an order, those items are immediately pulled from our available stock and reserved exclusively for you, making them unavailable to other customers.</li>
                <li><strong>Resource Allocation:</strong> For many of our services/products, labor and materials are committed the moment the transaction is finalized. Reversing these steps incurs significant operational costs that would ultimately lead to higher prices for everyone.</li>
              </ul>

              <h3 className="text-lg font-bold text-foreground mt-6 mb-3">A Note to Our Customers:</h3>
              <p className="mb-4">
                We want you to be completely satisfied with your purchase! To ensure a seamless experience, we kindly ask that you thoroughly review all aspects of your order before clicking the "Place Order" button.
              </p>
              
              <p className="mb-2"><strong>Before you finalize your purchase, please double-check:</strong></p>
              <ul className="list-disc pl-5 mb-4 space-y-1">
                <li><strong>Product Specifications:</strong> Are the size, color, and quantity correct?</li>
                <li><strong>Shipping Details:</strong> Is your delivery address and contact information up to date?</li>
                <li><strong>Compatibility:</strong> If applicable, does the item meet your specific technical or personal requirements?</li>
              </ul>
              <p>
                Taking an extra moment to verify these details helps us get your order to you exactly as you envisioned it.
              </p>
            </div>
          </section>

          {/* Terms & Conditions */}
          <section id="terms" className="bg-white border border-border p-8 scroll-mt-32">
            <h2 className="text-2xl font-black uppercase mb-2">Terms & Conditions</h2>
            <p className="text-xs text-muted-foreground mb-6">Last updated on Dec 25, 2025</p>
            
            <div className="prose prose-sm max-w-none text-muted-foreground">
              <p className="mb-4">
                For the purpose of these Terms and Conditions, The term "we", "us", "our" used anywhere on this page shall mean MODULAR FURNITURE STORE, whose registered/operational office is in Mumbai City MUMBAI Maharashtra India. "you", "your", "user", "visitor" shall mean any natural or legal person who is visiting our website and/or agreed to purchase from us.
              </p>
              
              <p className="mb-4">
                <strong>Your use of the website and/or purchase from us are governed by following Terms and Conditions:</strong>
              </p>

              <ul className="list-disc pl-5 space-y-3">
                <li>The content of the pages of this website is subject to change without notice. Neither we nor any third parties provide any warranty or guarantee as to the accuracy, timeliness, performance, completeness or suitability of the information and materials found or offered on this website for any particular purpose.</li>
                <li>You acknowledge that such information and materials may contain inaccuracies or errors and we expressly exclude liability for any such inaccuracies or errors to the fullest extent permitted by law.</li>
                <li>Your use of any information or materials on our website and/or product pages is entirely at your own risk, for which we shall not be liable. It shall be your own responsibility to ensure that any products, services or information available through our website and/or product pages meet your specific requirements.</li>
                <li>Our website contains material which is owned by or licensed to us. This material includes, but are not limited to, the design, layout, look, appearance and graphics. Reproduction is prohibited other than in accordance with the copyright notice, which forms part of these terms and conditions.</li>
                <li>All trademarks reproduced in our website which are not the property of, or licensed to, the operator are acknowledged on the website. Unauthorized use of information provided by us shall give rise to a claim for damages and/or be a criminal offense.</li>
                <li>From time to time our website may also include links to other websites. These links are provided for your convenience to provide further information. You may not create a link to our website from another website or document without MODULAR FURNITURE STORE's prior written consent.</li>
                <li>Any dispute arising out of use of our website and/or purchase with us and/or any engagement with us is subject to the laws of India.</li>
                <li>We shall be under no liability whatsoever in respect of any loss or damage arising directly or indirectly out of the decline of authorization for any Transaction, on Account of the Cardholder having exceeded the preset limit mutually agreed by us with our acquiring bank from time to time.</li>
              </ul>
            </div>
          </section>

          {/* Contact Us */}
          <section id="contact" className="bg-white border border-border p-8 scroll-mt-32">
            <h2 className="text-2xl font-black uppercase mb-2">Contact Us</h2>
            <p className="text-xs text-muted-foreground mb-6">Last updated on Dec 25, 2025</p>
            
            <div className="prose prose-sm max-w-none text-muted-foreground">
              <p className="mb-4">You may contact us using the information below:</p>
              <div className="bg-secondary p-4">
                <p className="mb-2"><strong>Merchant Legal Entity Name:</strong> MODULAR FURNITURE STORE</p>
                <p className="mb-2"><strong>Telephone No:</strong> 9820596811</p>
                <p><strong>E-Mail ID:</strong> hello@modularfurniturestore.com</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
